import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Download, FileText, Loader2, Printer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import {
  buildFunctionUrl,
  getSupabasePublishableKey,
} from '@/lib/selfHostedUrls';
import {
  officialInvoiceFilename,
  officialInvoiceSlugToNumber,
  type CustomerInvoicePortal,
} from '../../supabase/functions/_shared/officialInvoiceCustomerRoute';

interface CustomerInvoiceProps {
  portal: CustomerInvoicePortal;
}

function invoiceDocumentUrl(slug: string) {
  const url = new URL(buildFunctionUrl('view-official-invoice'));
  url.pathname = `${url.pathname.replace(/\/$/, '')}/${encodeURIComponent(slug)}`;
  return url.toString();
}

export default function CustomerInvoice({ portal }: CustomerInvoiceProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { invoiceNumberSlug = '' } = useParams();
  const invoiceNumber = officialInvoiceSlugToNumber(invoiceNumberSlug);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'notFound' | 'unavailable' | null>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const isSeek = portal === 'seek';
  const brandName = isSeek ? 'ForkSeek' : 'ForkHire';
  const brandMark = isSeek ? '/brand/forkseek/mark.svg' : '/brand/forkhire/mark.svg';
  const backPath = isSeek ? '/seek/settings/invoices' : '/settings/billing/invoices';

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;

    const loadPdf = async () => {
      if (!invoiceNumber) {
        setError('notFound');
        setLoading(false);
        return;
      }

      const client = isSeek ? seekSupabase : supabase;
      const { data: { session } } = await client.auth.getSession();
      if (!session?.access_token) {
        setError('notFound');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(invoiceDocumentUrl(invoiceNumberSlug), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            apikey: getSupabasePublishableKey(),
            Accept: 'application/pdf',
          },
          credentials: 'omit',
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) {
          setError(response.status === 404 || response.status === 401 ? 'notFound' : 'unavailable');
          return;
        }
        const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';
        if (!contentType.startsWith('application/pdf')) {
          setError('unavailable');
          return;
        }
        const pdf = await response.blob();
        objectUrl = URL.createObjectURL(pdf);
        setPdfUrl(objectUrl);
      } catch (requestError) {
        if (!(requestError instanceof DOMException && requestError.name === 'AbortError')) {
          setError('unavailable');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void loadPdf();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [invoiceNumber, invoiceNumberSlug, isSeek, portal]);

  const downloadPdf = () => {
    if (!pdfUrl || !invoiceNumber) return;
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = officialInvoiceFilename(invoiceNumber);
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const printPdf = () => frameRef.current?.contentWindow?.print();

  return (
    <main className="min-h-screen bg-muted/30 px-4 py-5 sm:px-6 sm:py-8">
      <section className="mx-auto flex min-h-[calc(100dvh-2.5rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm sm:min-h-[calc(100dvh-4rem)]">
        <header className="flex flex-col gap-4 border-b border-border px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('officialInvoiceDocument.back')}
              onClick={() => navigate(backPath)}
            >
              <ArrowLeft className="h-5 w-5 rtl:rotate-180" />
            </Button>
            <img src={brandMark} alt="" className="h-10 w-10 object-contain" />
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">{brandName}</p>
              <h1 className="truncate text-lg font-bold text-foreground sm:text-xl">{t('officialInvoiceDocument.title')}</h1>
              {invoiceNumber && <p className="font-mono text-sm text-muted-foreground">{invoiceNumber}</p>}
            </div>
          </div>

          {pdfUrl && (
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <Button type="button" variant="outline" className="gap-2" onClick={downloadPdf}>
                <Download className="h-4 w-4" />
                {t('officialInvoiceDocument.download')}
              </Button>
              <Button type="button" className="gap-2" onClick={printPdf}>
                <Printer className="h-4 w-4" />
                {t('officialInvoiceDocument.print')}
              </Button>
            </div>
          )}
        </header>

        <div className="relative flex min-h-[65vh] flex-1 items-center justify-center bg-muted/40 p-3 sm:p-5">
          {loading && (
            <div className="flex flex-col items-center gap-3 text-center text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p>{t('officialInvoiceDocument.loading')}</p>
            </div>
          )}

          {!loading && error && (
            <div className="max-w-md rounded-2xl border border-border bg-background p-8 text-center shadow-sm">
              <FileText className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
              <h2 className="text-lg font-semibold text-foreground">
                {t(`officialInvoiceDocument.${error}Title`)}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {t(`officialInvoiceDocument.${error}Description`)}
              </p>
              <Button type="button" variant="outline" className="mt-5" onClick={() => navigate(backPath)}>
                {t('officialInvoiceDocument.backToInvoices')}
              </Button>
            </div>
          )}

          {!loading && pdfUrl && invoiceNumber && (
            <iframe
              ref={frameRef}
              src={pdfUrl}
              title={t('officialInvoiceDocument.frameTitle', { number: invoiceNumber })}
              className="h-[72vh] w-full rounded-xl border border-border bg-white sm:h-[78vh]"
            />
          )}
        </div>
      </section>
    </main>
  );
}
